/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { useCallback, useEffect, useState, type ComponentType } from 'react';
import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import { MenuIdSearch } from '@jira/navigation-apps__constants/src/MenuIdSearch.tsx';
import { PACKAGE_NAME } from '@jira/navigation-apps__atlassian-navigation-ui/src/constants.tsx';
import { useTopMenus as useTopMenusDI } from '@jira/navigation-apps__common/src/controllers/top-menus/index.tsx';
import Pressable from '@atlaskit/primitives/pressable';
import { xcss } from '@atlaskit/primitives/xcss';
import { defineMessage, useIntl } from '@atlassian/jira-intl';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { lazyForPaint, type LazyComponent } from '@atlassian/react-loosely-lazy';
import { QuickFindSkeleton as QuickFindSkeletonInternal } from '@atlassian/search-page/quick-find-skeleton';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { useQueryParam, useRouterActions } from '@atlassian/react-resource-router';
import {
	searchDialogMetric,
	SEARCH_DIALOG_DOWNLOAD_START_MARK,
	SEARCH_DIALOG_DOWNLOAD_END_MARK,
} from './performance-metrics/index.tsx';
import type { SearchProps as SearchPropsType } from './types.tsx';
import { SkeletonInteractive } from './skeleton/interactive.tsx';
import {
	SearchLoadOnIntentMetricEnd,
	searchLoadOnIntentMetricStartFromSkeleton,
} from './performance-metrics/search-load-on-intent.tsx';
import { buildJiraAdvancedSearchUrl, useRovoSearchEnabled } from './utils.tsx';

const interactiveSearchSkeletonPlaceholderMessage = defineMessage({
	defaultMessage: 'Search',
	id: 'atlassian-navigation.search.interactive-search-skeleton-placeholder',
	description:
		'Placeholder for the interactive skeleton searchbar. Text is shown until user types in the search bar.',
});

export const QuickFindSkeleton = ({
	setShowRealExperience,
	injectValue,
	useTopMenus = useTopMenusDI,
}: Pick<SearchPropsType, 'useTopMenus'> & {
	setShowRealExperience?: (showRealExperience: boolean) => void;
	injectValue?: string;
}): React.JSX.Element => {
	const [_, { on: openMenu }] = useTopMenus(MenuIdSearch);
	const { push } = useRouterActions();
	const onClick = useCallback(() => {
		searchLoadOnIntentMetricStartFromSkeleton();
		setShowRealExperience?.(true);
		openMenu();
	}, [setShowRealExperience, openMenu]);

	const onMouseOver = useCallback(() => {
		setShowRealExperience?.(true);
	}, [setShowRealExperience]);

	const onEnter = useCallback(
		(e: React.KeyboardEvent) => {
			const target = e.target;
			if (target instanceof HTMLInputElement) {
				target.blur();
				const url = buildJiraAdvancedSearchUrl({
					query: target.value,
					// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
					siteUrl: window.location.origin,
				});
				push(url.href);
			}
		},
		[push],
	);

	return (
		<Pressable
			inputMode="search"
			onClick={onClick}
			onMouseOver={onMouseOver}
			onFocus={onClick}
			xcss={quickFindSkeletonStyle}
			testId="atlassian-navigation.ui.search.quickfind-skeleton-wrapper"
		>
			<QuickFindSkeletonInternal onEnter={onEnter} isNav4Enabled injectValue={injectValue} />
		</Pressable>
	);
};

const LoadingSkeletonInteractive = () => {
	const { formatMessage } = useIntl();
	return (
		<SkeletonInteractive placeholder={formatMessage(interactiveSearchSkeletonPlaceholderMessage)} />
	);
};

const SearchDialogMetricStart = () => {
	useEffect(() => {
		searchDialogMetric.startFromPageLoad();
		searchDialogMetric.mark(SEARCH_DIALOG_DOWNLOAD_START_MARK);
	}, []);

	return null;
};

const SearchDialogMetricEnd = () => {
	useEffect(() => {
		searchDialogMetric.mark(SEARCH_DIALOG_DOWNLOAD_END_MARK);
	}, []);

	return null;
};

// exported for testing purposes
// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
export const LazySearch: LazyComponent<React.ComponentType<SearchPropsType>> = lazyForPaint<
	ComponentType<SearchPropsType>
>(
	() =>
		import(/* webpackChunkName: "async-atlassian-navigation-search" */ './main.tsx').then(
			(m) => m.Search,
		),
	{ ssr: false },
);

export const SearchInner = (props: SearchPropsType): React.JSX.Element => {
	const [injectValue] = useQueryParam('text');
	const isRovoEnabled = useRovoSearchEnabled();
	const loadingSkeleton = <LoadingSkeletonExpandable useTopMenus={props.useTopMenus} />;

	const skeleton = isRovoEnabled ? (
		<QuickFindSkeleton useTopMenus={props.useTopMenus} injectValue={injectValue} />
	) : (
		loadingSkeleton
	);

	return (
		<JSErrorBoundary id="search" packageName={PACKAGE_NAME} fallback="unmount">
			<SearchDialogMetricStart />
			<Placeholder name="lazy-search" fallback={skeleton}>
				<LazySearch {...props} />
				<SearchDialogMetricEnd />
				<SearchLoadOnIntentMetricEnd />
			</Placeholder>
		</JSErrorBoundary>
	);
};

const loadingSkeletonExpandableWrapperStyle = { width: '100%' } as const; // only for nav4

export const LoadingSkeletonExpandable = ({
	setShowRealExperience,
	useTopMenus = useTopMenusDI,
}: Pick<SearchPropsType, 'useTopMenus'> & {
	setShowRealExperience?: (showRealExperience: boolean) => void;
}): React.JSX.Element => {
	const [_, { on: openMenu }] = useTopMenus(MenuIdSearch);
	const onClick = useCallback(() => {
		searchLoadOnIntentMetricStartFromSkeleton();
		setShowRealExperience?.(true);
		openMenu();
	}, [setShowRealExperience, openMenu]);

	const onMouseOver = useCallback(() => {
		setShowRealExperience?.(true);
	}, [setShowRealExperience]);
	return (
		// This is being suppressed as it is a light wrapper around the loading skeleton interactive component which is used as an intent sensor to pre-warm the search dialog.
		// eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable, @atlassian/a11y/no-static-element-interactions
		<div
			data-testid="atlassian-navigation.ui.search.loading-skeleton-expandable"
			onClick={onClick}
			onKeyDown={onClick}
			onMouseOver={onMouseOver}
			onFocus={onClick} // we use onClick instead of onMouseOver to expand the dialog on focus as the interactive skeleton focus expands - if we dont expand we get a flicker
			// eslint-disable-next-line jira/react/no-style-attribute, @atlaskit/ui-styling-standard/enforce-style-prop
			style={loadingSkeletonExpandableWrapperStyle}
		>
			<LoadingSkeletonInteractive />
		</div>
	);
};

export const SearchLoadOnIntent = (props: SearchPropsType): React.JSX.Element => {
	const [injectValue] = useQueryParam('text');

	const { useTopMenus = useTopMenusDI } = props;

	const [isSearchMenuOpen] = useTopMenus(MenuIdSearch);
	const [showRealExperience, setShowRealExperience] = useState(isSearchMenuOpen);
	const isRovoEnabled = useRovoSearchEnabled();

	useEffect(() => {
		if (isSearchMenuOpen) {
			setShowRealExperience(true);
		}
	}, [isSearchMenuOpen, setShowRealExperience]);

	return (
		<UFOSegment name="atlassian-navigation-search">
			{showRealExperience ? (
				<UFOSegment name="atlassian-navigation-search-experience">
					<SearchInner {...props} />
				</UFOSegment>
			) : (
				<UFOSegment name="atlassian-navigation-search-loading-skeleton-expandable">
					{isRovoEnabled ? (
						<QuickFindSkeleton
							useTopMenus={useTopMenus}
							setShowRealExperience={setShowRealExperience}
							injectValue={injectValue}
						/>
					) : (
						<LoadingSkeletonExpandable
							useTopMenus={useTopMenus}
							setShowRealExperience={setShowRealExperience}
						/>
					)}
				</UFOSegment>
			)}
		</UFOSegment>
	);
};

export const Search: (props: SearchPropsType) => React.JSX.Element = SearchLoadOnIntent;

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export type { SearchProps } from './types';

const quickFindSkeletonStyle = xcss({
	backgroundColor: 'color.background.neutral.subtle',
	width: '100%',
	padding: 'space.0',
});
